import type { SxProps, Theme } from '@mui/material';
import type React from 'react';

import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';

import { themeColors } from '@/config/theme/themeColors.ts';
import { useIsMobile } from '@/layouts/private/sidebar/hooks/useResponsiveSideBar.ts';
import { useBreadcrumbs } from '@/layouts/private/topBar/breadcrumbs/useBreadcrumbs.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

/**
 * TopBar heading (OCD-305): on desktop a clickable breadcrumb trail derived
 * from route declarations, on mobile only the current page title. Replaces
 * the previous imperative page-title state. The current page shows the
 * entity's display name when the query cache holds it (OCD-311), falling
 * back to the static label while data is missing.
 *
 * MUI Breadcrumbs paints its root with `text.secondary` and `body1`, which
 * washed out both levels. Override: parent levels f16Medium and current page
 * f20Medium, all in darkGrey — small size step, readable contrast.
 */

const breadcrumbsSx: SxProps<Theme> = {
  '& .MuiBreadcrumbs-separator': {
    color: themeColors.grey,
    mx: 1,
  },
  color: themeColors.darkGrey,
};

const parentLinkSx: SxProps<Theme> = {
  '&:hover': {
    color: themeColors.primary,
  },
  color: themeColors.darkGrey,
};

const currentPageSx: SxProps<Theme> = {
  color: themeColors.black,
};
export const TopBarBreadcrumbs: React.FC = () => {
  const trail = useBreadcrumbs();
  const isMobile = useIsMobile();
  const { t } = useTranslationWithPrefix('pageTitles');

  if (trail.length === 0) {
    return null;
  }

  const current = trail[trail.length - 1];

  if (isMobile) {
    return (
      <Typography noWrap sx={{ color: themeColors.darkGrey }} variant="f24Medium">
        {current.entityName ?? t(current.title)}
      </Typography>
    );
  }

  return (
    <Breadcrumbs aria-label="breadcrumb" maxItems={4} sx={breadcrumbsSx}>
      {trail.map((item, index) => {
        const isCurrent = index === trail.length - 1;
        if (isCurrent) {
          return (
            <Typography key={item.path} noWrap sx={currentPageSx} variant="f20Medium">
              {item.entityName ?? t(item.title)}
            </Typography>
          );
        }

        return (
          <Link
            color="inherit"
            component={RouterLink}
            key={item.path}
            sx={parentLinkSx}
            to={item.path}
            underline="hover"
            variant="f16Medium"
          >
            {t(item.title)}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
};
